import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, tap } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { TranslocoService } from '@jsverse/transloco';
import { SharedApiService, ApiType } from '../../../../../libs/shared/src/lib/services/api.service';
import { UserFromBE, UserPreferences } from '../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { BodyUserDataResponse } from '../models/responses/body-user-response.model';

export interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  cityCountry?: string;
  password: string;
  inviteCode?: string;
  aiMatching?: boolean;
  newsletter?: boolean;
  termsAccepted: boolean;
}

export class BodyNullResponse {
  body?: null;
  message?: string;
  status?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<UserFromBE | null>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserFromBE | null>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private readonly platformId = inject(PLATFORM_ID);

  loginURL = 'api/login';
  authUserURL = 'api/auth/user';
  verifyEmailURL = '/email-verify';
  registerURL = 'api/auth/registration-candidate/user';
  googleRegisterURL = 'api/auth/google-registration/user';
  forgotPasswordURL = 'api/request-password-reset';
  validateResetTokenURL = 'api/reset-password-token-validator';
  resetPasswordURL = 'api/reset-password';
  questionsDataURL = 'api/auth/registration-candidate/user';

  userFromBE: UserFromBE | null = null;

  constructor(
    private readonly sharedApiService: SharedApiService,
    private readonly router: Router,
    private readonly fireAuth: AngularFireAuth,
    private readonly ngZone: NgZone,
    private readonly translocoService: TranslocoService,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserFromBE | null>(null);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    // Initialize user from localStorage if available (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      const user = localStorage.getItem('user');
      if (user) {
        this.currentUserSubject.next(JSON.parse(user));
        this.userFromBE = JSON.parse(user);
      }

      // SAM-027: cross-tab logout sync. When another tab calls fireAuth.signOut(),
      // Firebase's IndexedDB-based auth state replicates to this tab and emits null.
      // We then clean up local app state so this tab doesn't continue showing a
      // logged-in UI with a dead session.
      this.fireAuth.authState.subscribe((user) => {
        if (!user && this.currentUserSubject.value !== null) {
          // Firebase reports no user but we still have local state → another tab logged out.
          this.ngZone.run(() => this.cleanupLocalSession());
        }
      });
    }
  }

  /**
   * Clears local app state without calling Firebase signOut (since this is
   * called in response to Firebase already being signed out by another tab).
   * Use logout() for user-initiated sign-out.
   */
  private cleanupLocalSession(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('user');
      localStorage.removeItem('preferences');
      localStorage.removeItem('session');
    }
    this.currentUserSubject.next(null);
    this.userFromBE = null;
    this.router.navigate(['/']);
  }

  get currentUserValue(): UserFromBE | null {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserFromBE | null) {
    this.currentUserSubject.next(user);
  }

  // Using ApiService for headers now
  isLoggedIn(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const user = localStorage.getItem('user');
      return user !== null;
    }
    return false;
  }

  login(idToken: string): Observable<UserFromBE> {
    this.isLoadingSubject.next(true);
    return this.loginUser(idToken).pipe(
      map((res: UserFromBE) => {
        this.userFromBE = res;
        this.currentUserValue = res;

        if (isPlatformBrowser(this.platformId)) {
          const userPreferences = res.userPreferences as unknown as UserPreferences;
          const userLanguage = this.userFromBE.language || 'en';

          localStorage.setItem('selectedLanguage', userLanguage);
          localStorage.setItem('user', JSON.stringify(this.userFromBE));
          localStorage.setItem('preferences', userPreferences ? JSON.stringify(userPreferences) : '{}');
          localStorage.setItem('session', idToken);
        }

        this.isLoadingSubject.next(false);
        return res;
      }),
      catchError((err): Observable<UserFromBE> => {
        this.isLoadingSubject.next(false);
        return throwError(() => err);
      })
    );
  }

  loginUser(idToken: string): Observable<UserFromBE> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('type', 'candidate');

    let headers = this.sharedApiService.getHTTPHeaders(true);
    headers = headers.append('Authorization', idToken);

    return this.sharedApiService.post<any>(this.loginURL, {}, queryParams, { apiType: ApiType.OPEN, jobsRequest: true, headers }).pipe(
      map((response) => {
        // Handle the response based on its actual structure
        if (response?.body) {
          return response.body as UserFromBE;
        } else {
          // If the response is directly the user data
          return response as UserFromBE;
        }
      })
    );
  }

  /**
   * Fetches the current user data from the server
   * @returns Observable with the current user data
   */
  getCurrentUserBE(): Observable<UserFromBE> {
    return this.sharedApiService.get<UserFromBE>(this.authUserURL, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes the current user data from the server and updates local storage
   * @returns Observable that completes when the user data is refreshed
   */
  refreshCurrentUser(): Observable<UserFromBE> {
    return this.getCurrentUserBE().pipe(
      tap((updatedUser: UserFromBE) => {
        // Update the current user value
        this.currentUserValue = updatedUser;
        this.userFromBE = updatedUser;

        // Update localStorage with the new user data
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }),
      catchError((error) => {
        console.error('Error refreshing current user:', error);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    // Sign out of Firebase first so cross-tab listeners in other tabs propagate.
    // We don't await — the cleanup runs regardless of Firebase signOut success.
    if (isPlatformBrowser(this.platformId)) {
      this.fireAuth.signOut().catch(() => {
        // Firebase signOut can fail if already signed out or offline.
        // Local cleanup runs regardless.
      });
    }
    this.cleanupLocalSession();
  }

  verifyEmail(token: string): Observable<BodyUserDataResponse> {
    return this.sharedApiService.put<BodyUserDataResponse>(`${this.verifyEmailURL}?token=${token}`, {}, undefined, {
      apiType: ApiType.DEFAULT,
      jobsRequest: true,
    });
  }

  getQuestionsData(userId: string): Observable<any> {
    return this.sharedApiService
      .get<any>(`${this.questionsDataURL}?id=${userId}`, undefined, { apiType: ApiType.OPEN, jobsRequest: true })
      .pipe(map((response) => response));
  }

  /**
   * Request a password reset email
   * @param email The email address to send the reset link to
   */
  forgotPassword(email: string): Observable<{ message: string }> {
    this.isLoadingSubject.next(true);

    return this.sharedApiService
      .post<{ message: string }>(`${this.forgotPasswordURL}?email=${encodeURIComponent(email)}&type=candidate`, {}, undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        catchError((error) => {
          let errorMessage = this.translocoService.translate('auth.forgotPassword.errors.generic');

          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 404) {
            errorMessage = this.translocoService.translate('auth.forgotPassword.errors.noAccount');
          } else if (error.status === 0) {
            errorMessage = this.translocoService.translate('auth.forgotPassword.errors.connection');
          }

          return throwError(() => new Error(errorMessage));
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  /**
   * Validate the reset password token
   * @param token The reset token to validate
   */
  validateResetToken(token: string): Observable<{ valid: boolean }> {
    return this.sharedApiService
      .post<{ valid: boolean }>(`${this.validateResetTokenURL}?token=${encodeURIComponent(token)}&type=candidate`, {}, undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        catchError((error) => {
          let errorMessage = 'Invalid or expired token.';

          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 400) {
            errorMessage = 'Invalid token format.';
          } else if (error.status === 404) {
            errorMessage = 'Token not found or expired.';
          } else if (error.status === 0) {
            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  /**
   * Reset user password using token
   * @param token The reset token received via email
   * @param newPassword The new password to set
   */
  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    this.isLoadingSubject.next(true);

    return this.sharedApiService
      .post<{ message: string }>(
        `${this.resetPasswordURL}?token=${encodeURIComponent(token)}&password=${encodeURIComponent(newPassword)}&type=candidate`,
        {},
        undefined,
        { apiType: ApiType.OPEN, jobsRequest: true }
      )
      .pipe(
        catchError((error) => {
          let errorMessage = 'An error occurred while resetting your password.';

          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 400) {
            errorMessage = 'Invalid request. Please check your information and try again.';
          } else if (error.status === 404) {
            errorMessage = 'Token not found or expired.';
          } else if (error.status === 0) {
            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
          }

          return throwError(() => new Error(errorMessage));
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  // get current user from local storage
  getCurrentUser(): UserFromBE | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    }
    return null;
  }

  /**
   * Register a new user
   * @param registrationData User registration data
   * @param jobId Job ID (optional)
   * @returns Observable with registration response
   */

  registerCandidate(data: UserFromBE, jobId?: number): Observable<BodyNullResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('jobId', jobId ? jobId : '');

    return this.sharedApiService.post<BodyNullResponse>(this.questionsDataURL, JSON.stringify(data), queryParams, { apiType: ApiType.OPEN, jobsRequest: true });
  }

  /**
   * Update auth token on the server
   * @param token New Firebase ID token
   * @returns Observable with response
   */
  updateAuthToken(token: string): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('token', token);

    let headers = this.sharedApiService.getHTTPHeaders(true);

    const data = {
      token: token,
    };

    return this.sharedApiService.put<any>('api/auth/update-auth-token', data, queryParams, { apiType: ApiType.OPEN, jobsRequest: true, headers });
  }

  /**
   * Register or check if Google user exists
   * @param user Google user data
   * @returns Observable with 'NOT_REGISTERED' or 'REGISTERED' status
   */
  googleRegister(user: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.sharedApiService
      .post<any>(this.googleRegisterURL, user, undefined, { apiType: ApiType.OPEN, jobsRequest: true })
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  /**
   * Update user language preference on the server
   * @param language Language code (en, sk, cz)
   * @returns Observable with response
   */
  updateUserLanguage(language: string): Observable<{ message: string }> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('language', language);

    return this.sharedApiService.put<{ message: string }>('api/auth/user/language', {}, queryParams, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      catchError((error) => {
        console.error('Error updating user language:', error);
        return throwError(() => error);
      })
    );
  }
}
